import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { TrendingUp, DollarSign, Receipt, ArrowDown, Percent, Layers } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BusinessAnalysis() {
  const token = localStorage.getItem('vendor_token');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);

  const fetchAnalysis = useCallback(async () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const res = await fetch(`${API}/api/lis/business/analysis?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    setData(await res.json());
  }, [dateFrom, dateTo, token]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  if (!data) return <div className="flex items-center justify-center h-64 text-slate-500">Loading...</div>;
  const s = data.summary || {};

  const metrics = [
    { label: 'Revenue', value: `Rs.${(s.total_revenue || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-blue-600 bg-blue-50' },
    { label: 'Received', value: `Rs.${(s.total_received || 0).toLocaleString()}`, icon: ArrowDown, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Due', value: `Rs.${(s.total_due || 0).toLocaleString()}`, icon: DollarSign, color: 'text-red-600 bg-red-50' },
    { label: 'B2B Cost', value: `Rs.${(s.total_b2b || 0).toLocaleString()}`, icon: Layers, color: 'text-purple-600 bg-purple-50' },
    { label: 'Expenses', value: `Rs.${(s.total_expenses || 0).toLocaleString()}`, icon: Receipt, color: 'text-orange-600 bg-orange-50' },
    { label: 'Net Profit', value: `Rs.${(s.net_profit || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-green-600 bg-green-50' },
    { label: 'Margin', value: `${s.profit_margin || 0}%`, icon: Percent, color: 'text-indigo-600 bg-indigo-50' },
    { label: 'Bills', value: s.total_bills || 0, icon: Receipt, color: 'text-slate-600 bg-slate-100' },
  ];

  return (
    <div data-testid="lis-business-analysis">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Business Analysis</h1>
        <div className="flex gap-2"><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px] h-9" /><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px] h-9" /></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {metrics.map((m, i) => { const Icon = m.icon; return (
          <Card key={`card-${i}`} className="border-slate-200"><CardContent className="p-4"><div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${m.color}`}><Icon className="w-4 h-4" /></div><p className="text-xs text-slate-500">{m.label}</p><p className="text-lg font-bold text-slate-800">{m.value}</p></CardContent></Card>
        ); })}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {data.payment_modes?.length > 0 && (
          <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Payment Modes</CardTitle></CardHeader><CardContent>
            {data.payment_modes.map((m, i) => (
              <div key={`el-${i}`} className="flex justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm"><span className="capitalize">{m.mode}</span><span className="font-medium">Rs.{m.total.toLocaleString()} ({m.count})</span></div>
            ))}
          </CardContent></Card>
        )}
        {data.top_tests?.length > 0 && (
          <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Top Tests</CardTitle></CardHeader><CardContent>
            {data.top_tests.slice(0, 10).map((t, i) => (
              <div key={`el-${i}`} className="flex justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm"><span className="text-slate-700">{t.name}</span><span className="text-xs text-slate-500">{t.count}x | Rs.{t.revenue.toLocaleString()}</span></div>
            ))}
          </CardContent></Card>
        )}
        {data.expense_categories?.length > 0 && (
          <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Expense Breakdown</CardTitle></CardHeader><CardContent>
            {data.expense_categories.map((c, i) => (
              <div key={`el-${i}`} className="flex justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm"><span>{c.category}</span><span className="font-medium">Rs.{c.total.toLocaleString()}</span></div>
            ))}
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
